/**
 * VCI-P1 Slice 2 — Internal Venue Partner Application Form
 * Route: /venue-commerce/partner-applications/new
 *
 * Internal form to submit a venue partner application on behalf of a venue owner.
 * External submissions are disabled until deployment gate passes.
 * Consent capture is mandatory.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  FileText,
  Shield,
  ArrowLeft,
  Loader2,
  CheckCircle,
  AlertTriangle,
  Info,
  Hotel,
  MapPin,
  Users,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

const API = process.env.REACT_APP_BACKEND_URL;

const VENUE_TYPES = ["FARMHOUSE","RESORT","GARDEN","BANQUET_HALL","HOTEL","HERITAGE","OTHER"];

function FieldGroup({ label, required, hint, children }) {
  return (
    <div className="space-y-1">
      <label className="text-sm font-medium text-gray-700">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
      {hint && <p className="text-xs text-gray-400">{hint}</p>}
    </div>
  );
}

function SectionHeading({ icon: Icon, title, subtitle }) {
  return (
    <div className="flex items-center gap-3 mb-4 pb-3 border-b border-gray-100">
      <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
        <Icon size={16} className="text-indigo-600" />
      </div>
      <div>
        <p className="text-sm font-semibold text-gray-800">{title}</p>
        {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
      </div>
    </div>
  );
}

export default function VenuePartnerApplyForm() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [serverError, setServerError] = useState("");

  const [form, setForm] = useState({
    applicant_name: "",
    applicant_email: "",
    applicant_phone: "",
    applicant_designation: "",
    venue_name: "",
    venue_city: "",
    venue_type: "",
    company_name: "",
    capacity_estimate: "",
    events_per_year: "",
    website: "",
    gst_ref: "",
    additional_notes: "",
    consent_accepted: false,
  });

  const [errors, setErrors] = useState({});

  const set = (k, v) => {
    setForm(f => ({ ...f, [k]: v }));
    setErrors(e => ({ ...e, [k]: "" }));
  };

  const validate = () => {
    const errs = {};
    if (!form.applicant_name.trim()) errs.applicant_name = "Required";
    if (!form.applicant_email.trim()) errs.applicant_email = "Required";
    else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.applicant_email)) errs.applicant_email = "Invalid email";
    if (!form.applicant_phone.trim()) errs.applicant_phone = "Required";
    if (!form.venue_name.trim()) errs.venue_name = "Required";
    if (!form.venue_city.trim()) errs.venue_city = "Required";
    if (!form.consent_accepted) errs.consent_accepted = "Consent must be accepted";
    return errs;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    setSubmitting(true);
    setServerError("");
    try {
      const payload = {
        applicant_name:        form.applicant_name.trim(),
        applicant_email:       form.applicant_email.trim().toLowerCase(),
        applicant_phone:       form.applicant_phone.trim(),
        applicant_designation: form.applicant_designation.trim() || null,
        venue_name:            form.venue_name.trim(),
        venue_city:            form.venue_city.trim(),
        venue_type:            form.venue_type || null,
        company_name:          form.company_name.trim() || null,
        capacity_estimate:     form.capacity_estimate ? parseInt(form.capacity_estimate) : null,
        events_per_year:       form.events_per_year ? parseInt(form.events_per_year) : null,
        website:               form.website.trim() || null,
        gst_ref:               form.gst_ref.trim() || null,
        additional_notes:      form.additional_notes.trim() || null,
        consent: {
          accepted: true,
          timestamp: new Date().toISOString(),
          source: "INTERNAL_ENTRY",
        },
      };
      const res = await axios.post(`${API}/api/venue-commerce/partner-applications`, payload, { withCredentials: true });
      setSubmitted(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setServerError(typeof detail === "string" ? detail : JSON.stringify(detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-8 text-center space-y-4">
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
            <CheckCircle size={32} className="text-emerald-600" />
          </div>
          <h2 className="text-xl font-semibold text-emerald-800">Application Submitted</h2>
          <p className="text-sm text-emerald-700">
            Application number: <strong className="font-mono">{submitted.application_number}</strong>
          </p>
          {submitted.duplicate_check?.recommendation !== "CREATE_NEW" && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700 flex items-center gap-2">
              <AlertTriangle size={13} />
              Duplicate matches found — reviewer attention required.
            </div>
          )}
          <div className="flex gap-3 justify-center pt-2">
            <Button
              onClick={() => navigate(`/venue-commerce/partner-applications/${submitted.id}`)}
              data-testid="vci-view-submitted-app"
            >
              View Application
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/venue-commerce/partner-applications/new")}
              data-testid="vci-submit-another"
            >
              Submit Another
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate("/venue-commerce/partner-applications")}
          className="gap-1 text-gray-500 -ml-1" data-testid="vci-back-from-form">
          <ArrowLeft size={14} />
        </Button>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
            <FileText size={18} className="text-white" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-gray-900" data-testid="vci-apply-form-title">
              New Partner Application
            </h1>
            <p className="text-xs text-amber-600 flex items-center gap-1">
              <Shield size={10} /> Internal entry only — external applications disabled
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Section: Applicant */}
        <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm space-y-4">
          <SectionHeading icon={Users} title="Applicant" subtitle="Venue owner or representative details" />
          <div className="grid grid-cols-2 gap-4">
            <FieldGroup label="Full Name" required error={errors.applicant_name}>
              <Input value={form.applicant_name} onChange={e => set("applicant_name", e.target.value)}
                placeholder="e.g. Rajesh Kumar" className="h-9 text-sm" data-testid="vci-form-applicant-name" />
              {errors.applicant_name && <p className="text-xs text-red-500">{errors.applicant_name}</p>}
            </FieldGroup>
            <FieldGroup label="Designation">
              <Input value={form.applicant_designation} onChange={e => set("applicant_designation", e.target.value)}
                placeholder="e.g. Owner, Director" className="h-9 text-sm" data-testid="vci-form-applicant-designation" />
            </FieldGroup>
            <FieldGroup label="Email" required>
              <Input type="email" value={form.applicant_email} onChange={e => set("applicant_email", e.target.value)}
                placeholder="owner@venue.com" className="h-9 text-sm" data-testid="vci-form-applicant-email" />
              {errors.applicant_email && <p className="text-xs text-red-500">{errors.applicant_email}</p>}
            </FieldGroup>
            <FieldGroup label="Phone" required>
              <Input type="tel" value={form.applicant_phone} onChange={e => set("applicant_phone", e.target.value)}
                placeholder="+91 98765 43210" className="h-9 text-sm" data-testid="vci-form-applicant-phone" />
              {errors.applicant_phone && <p className="text-xs text-red-500">{errors.applicant_phone}</p>}
            </FieldGroup>
          </div>
        </div>

        {/* Section: Venue */}
        <div className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm space-y-4">
          <SectionHeading icon={Hotel} title="Venue Details" subtitle="Property and business information" />
          <div className="grid grid-cols-2 gap-4">
            <FieldGroup label="Venue Name" required>
              <Input value={form.venue_name} onChange={e => set("venue_name", e.target.value)}
                placeholder="e.g. Heritage Garden Hyderabad" className="h-9 text-sm" data-testid="vci-form-venue-name" />
              {errors.venue_name && <p className="text-xs text-red-500">{errors.venue_name}</p>}
            </FieldGroup>
            <FieldGroup label="City" required>
              <Input value={form.venue_city} onChange={e => set("venue_city", e.target.value)}
                placeholder="e.g. Hyderabad" className="h-9 text-sm" data-testid="vci-form-venue-city" />
              {errors.venue_city && <p className="text-xs text-red-500">{errors.venue_city}</p>}
            </FieldGroup>
            <FieldGroup label="Venue Type">
              <select value={form.venue_type} onChange={e => set("venue_type", e.target.value)}
                className="w-full h-9 border border-gray-200 rounded-lg px-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                data-testid="vci-form-venue-type">
                <option value="">— Select —</option>
                {VENUE_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g," ")}</option>)}
              </select>
            </FieldGroup>
            <FieldGroup label="Company Name" hint="Legal entity name if different from venue">
              <Input value={form.company_name} onChange={e => set("company_name", e.target.value)}
                placeholder="e.g. Heritage Events Pvt Ltd" className="h-9 text-sm" data-testid="vci-form-company-name" />
            </FieldGroup>
            <FieldGroup label="Capacity (guests)">
              <Input type="number" min={0} value={form.capacity_estimate} onChange={e => set("capacity_estimate", e.target.value)}
                placeholder="e.g. 500" className="h-9 text-sm" data-testid="vci-form-capacity" />
            </FieldGroup>
            <FieldGroup label="Events per Year">
              <Input type="number" min={0} value={form.events_per_year} onChange={e => set("events_per_year", e.target.value)}
                placeholder="e.g. 20" className="h-9 text-sm" data-testid="vci-form-events-per-year" />
            </FieldGroup>
            <FieldGroup label="Website">
              <Input value={form.website} onChange={e => set("website", e.target.value)}
                placeholder="https://..." className="h-9 text-sm" data-testid="vci-form-website" />
            </FieldGroup>
            <FieldGroup label="GST Number">
              <Input value={form.gst_ref} onChange={e => set("gst_ref", e.target.value)}
                placeholder="e.g. 36AABCH1234A1Z5" className="h-9 text-sm" data-testid="vci-form-gst-ref" />
            </FieldGroup>
          </div>
          <FieldGroup label="Additional Notes">
            <Textarea value={form.additional_notes} onChange={e => set("additional_notes", e.target.value)}
              placeholder="Any additional information about the venue or partnership..." rows={3}
              className="text-sm resize-none" data-testid="vci-form-additional-notes" />
          </FieldGroup>
        </div>

        {/* Consent */}
        <div className={`bg-white border rounded-xl p-5 shadow-sm ${errors.consent_accepted ? "border-red-300" : "border-gray-100"}`}>
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="consent"
              checked={form.consent_accepted}
              onChange={e => set("consent_accepted", e.target.checked)}
              className="w-4 h-4 rounded accent-indigo-600 mt-0.5 flex-shrink-0"
              data-testid="vci-form-consent"
            />
            <div>
              <label htmlFor="consent" className="text-sm font-medium text-gray-700 cursor-pointer">
                I confirm that this application is submitted on behalf of the venue owner with their explicit consent,
                and that the information provided is accurate to the best of my knowledge.
              </label>
              <p className="text-xs text-gray-400 mt-1">
                Source: INTERNAL_ENTRY · Submitted by Prashasta staff
              </p>
              {errors.consent_accepted && (
                <p className="text-xs text-red-500 mt-1">{errors.consent_accepted}</p>
              )}
            </div>
          </div>
        </div>

        {serverError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700" data-testid="vci-form-error">
            {serverError}
          </div>
        )}

        <div className="flex items-center justify-between pt-2">
          <Button type="button" variant="outline" onClick={() => navigate("/venue-commerce/partner-applications")}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting} className="gap-2 min-w-[140px]" data-testid="vci-form-submit">
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
            Submit Application
          </Button>
        </div>
      </form>
    </div>
  );
}
